
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleEliminationBracket } from '@g-loot/react-tournament-brackets';
import { useAppContext } from '../../AppContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import LoadingSpinner from '../LoadingSpinner';

const Match = ({ match }) => {
    const { t } = useTranslation();
    return (
        <div className="text-sm text-white">
            {match.participants.map((p, i) => (
                <div key={i} className={p.winner ? 'font-bold' : ''}>
                    {p.name || t('showdown.tournamentBracket.tbd')}
                </div>
            ))}
        </div>
    );
};

const TournamentBracket = ({ tournamentId }) => {
    const { t } = useTranslation();
    const { db, user } = useAppContext();
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const functions = getFunctions();

    useEffect(() => {
        if (!db || !tournamentId) {
            setLoading(false);
            return;
        }

        const tournamentRef = doc(db, 'artifacts/whispers-of-harmony/public/data/tournaments', tournamentId);
        const unsubscribe = onSnapshot(tournamentRef, (doc) => {
            if (doc.exists()) {
                setTournament({ id: doc.id, ...doc.data() });
            } else {
                setError(t('showdown.tournamentBracket.notFound'));
            }
            setLoading(false);
        }, (err) => {
            setError(err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, tournamentId, t]);

    const handleStartTournament = async () => {
        const startTournamentFn = httpsCallable(functions, 'startTournament');
        try {
            await startTournamentFn({ tournamentId });
        } catch (error) {
            console.error("Error starting tournament:", error);
        }
    };

    if (loading) return <LoadingSpinner message={t('showdown.tournamentBracket.loading')} />;
    if (error) return <p className="text-red-500">{error.message || error}</p>;
    if (!tournament) return null;

    // The @g-loot/react-tournament-brackets library expects a specific data structure for matches.
    // This is a placeholder structure. You will need to adapt your Firestore data to this format.
    const matches = [
        // ... (match data remains the same)
    ];

    return (
        <div className="p-8">
            <h1 className="text-4xl font-bold text-cyan-300 mb-8">{tournament.name}</h1>
            {tournament.creatorId === user.uid && tournament.status === 'open' && (
                <button 
                    onClick={handleStartTournament}
                    className="mb-8 px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-lg"
                >
                    {t('showdown.tournamentBracket.startTournament')}
                </button>
            )}
            {matches.length > 0 ? (
                <div className="w-full overflow-x-auto custom-scrollbar">
                    <SingleEliminationBracket matches={matches} matchComponent={Match} />
                </div>
            ) : (
                <p className="text-center text-gray-400">{t('showdown.tournamentBracket.notGenerated')}</p>
            )}
        </div>
    );
};

export default TournamentBracket;
